import Header from "./Header.jsx";
import {useState, useEffect, Suspense} from "react";
import ApiRequester from "./ApiRequester.jsx";
import {Chart, registerables} from "chart.js";
import annotationPlugin from 'chartjs-plugin-annotation';
import Footer from "./Footer.jsx";
import CampusDropDown from "./component-assets/CampusDropDown.jsx";
import TableHeaders from "./component-assets/TableHeaders.jsx";
import Button from "./component-assets/Button.jsx";
import {
    formSuccess,
    handleSubmitActions,
    showModal,
    getToday,
    formatDateIso,
    sortFunc,
    checkAll,
    hourToLocalizedHour, buildChartDataset, formatDate, formatDateNoYear, unpackFieldsToArray
} from "../libs.jsx";
import TableBody from "./component-assets/TableBody.jsx";
import MaterialIcon from "./component-assets/MaterialIcon.jsx";
import {ToastContainer} from "react-toastify";
import {CSVLink} from "react-csv";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import {useNavigate} from "react-router-dom";
import Navigation from "./Navigation.jsx";
import LineTrendingChart from "./charts/LineTrendingChart.jsx";
import ResidentTrendActions from "@/components/ResidentTrendActions.jsx";
import { getSession, setSession, getEhrInfo } from "../services/session";

const MainDash = () => {

    Chart.register(...registerables);
    Chart.register(annotationPlugin);

    let dData, resHeaders, careHeaders, areaHeaders, startDate, endDate, areaTypeHeaders, areaShiftHeaders,
        resCSVHeaders, careCSVHeaders;
    let colorlist = ["#2C7BE5", "#392CE5", "#05B2FF", "#A6C5F7", "#8989FF", "#D2DDEC", "#E52C7B", "#962CE5", "#E52CD8",
        "#E52C7B", "#E5392C", "#09BA00", "#0CFF00", "#8AFF85", "#AEFF00", "#F2FF00", "#A7B000"];
    let showAreaTypes = [1, 2, 3, 4];

    if (getSession() != null){
        dData = getSession();
    }


    careHeaders = [
        {label: "User Name", key: "display_name", align: "left", sorts: false, row: 1, rowSpan: true, rows: 2,
            link: {enabled: true, type: "standard", linkto: "/profiles/user", linkkey: "user_id"}},
        {label: "Average Daily Events", key: "", row: 1, colSpan: true, cols: 5},
        {label: `Total`, key: "avg_daily_events", align: "center", sorts: false, row: 2,
            type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: `${formatDateNoYear(getToday(-8))}-${formatDateNoYear(getToday(-1))}`,
            key: "week1", align: "center", sorts: false, row: 2, type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: `${formatDateNoYear(getToday(-14))}-${formatDateNoYear(getToday(-8))}`,
            key: "week2", align: "center", sorts: false, row: 2, type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: `${formatDateNoYear(getToday(-21))}-${formatDateNoYear(getToday(-14))}`,
            key: "week3", align: "center", sorts: false, row: 2, type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: `${formatDateNoYear(getToday(-28))}-${formatDateNoYear(getToday(-21))}`,
            key: "week4", align: "center", sorts: false, row: 2, type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: "Avg Response Rate (Min)", key: "avg_response_time", align: "center",
            sorts: false, row: 1, rowSpan: true, rows: 2, type: "nullsafe", nullValue: 0, nullReplace: "-"}
    ];

    careCSVHeaders = [
        {label: "User Name", key: "display_name"},
        {label: "Average Daily Alarms", key: "avg_daily_events"},
        {label: `Average Daily ${formatDate(getToday(-8))}-${formatDate(getToday(-1))}`, key: "week1"},
        {label: `Average Daily ${formatDate(getToday(-14))}-${formatDate(getToday(-8))}`, key: "week2"},
        {label: `Average Daily ${formatDate(getToday(-21))}-${formatDate(getToday(-14))}`, key: "week3"},
        {label: `Average Daily ${formatDate(getToday(-28))}-${formatDate(getToday(-21))}`, key: "week4"},
    ];

    areaHeaders = [
        {label: "Area Type", key: "area_type_name", sorts: false},
        {label: "Avg Daily Alarms", key: "avg_daily_events", align: "center", sorts: false},
        {label: "Avg Resp Time 0-7 Days", key: "week1", align: "center", sorts: false},
        {label: "Avg Resp Time 7-14 Days", key: "week2", align: "center", sorts: false},
        {label: "Avg Resp Time 14-21 Days", key: "week3", align: "center", sorts: false},
        {label: "Avg Resp Time 21-28 Days", key: "week4", align: "center", sorts: false},
        {label: "Avg Response Rate (Min)", key: "avg_response_time", align: "center", sorts: false}
    ];

    areaShiftHeaders = [
        {label: "Area Type", key: "area_type_name", sorts: false, row: 1, rowSpan: true, rows: 2},
        {label: "Day Shift", key: "", align: "center", row: 1, colSpan: true, cols: 2, bgColor: "#27B4FFFF"},
        {label: "Evening Shift", key: "", align: "center", row: 1, colSpan: true, cols: 2, bgColor: "#276BFFFF"},
        {label: "Night Shift", key: "", align: "center", row: 1, colSpan: true, cols: 2, bgColor: "#0B16FFFF"},
        {label: "Avg Daily Alarms", key: "avg_daily_events_1st", align: "center", sorts: false, row: 2, bgColor: "#27B4FFFF",
            type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: "Avg Response (Min)", key: "avg_response_time_1st", align: "center", sorts: false, row: 2, bgColor: "#27B4FFFF",
            type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: "Avg Daily Alarms", key: "avg_daily_events_2nd", align: "center", sorts: false, row: 2, bgColor: "#276BFFFF",
            type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: "Avg Response (Min)", key: "avg_response_time_2nd", align: "center", sorts: false, row: 2, bgColor: "#276BFFFF",
            type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: "Avg Daily Alarms", key: "avg_daily_events_3rd", align: "center", sorts: false, row: 2, bgColor: "#0B16FFFF",
            type: "nullsafe", nullValue: 0, nullReplace: "-"},
        {label: "Avg Response (Min)", key: "avg_response_time_3rd", align: "center", sorts: false, row: 2, bgColor: "#0B16FFFF",
            type: "nullsafe", nullValue: 0, nullReplace: "-"}
    ];

    areaTypeHeaders = [
        {label: "Area Type", key: "area_type_name", align: "left"},
        {label: "Description", key: "area_type_description", align: "left"},
    ];

    startDate = formatDateIso(getToday(-28));
    endDate = formatDateIso(getToday());

    const week1Range = `${formatDateNoYear(getToday(-8))}-${formatDateNoYear(getToday(-1))}`
    const week2Range = `${formatDateNoYear(getToday(-14))}-${formatDateNoYear(getToday(-8))}`
    const week3Range = `${formatDateNoYear(getToday(-21))}-${formatDateNoYear(getToday(-14))}`
    const week4Range = `${formatDateNoYear(getToday(-28))}-${formatDateNoYear(getToday(-21))}`

    // Data variables
    const [resOriginData, setResOriginData] = useState([]);
    const [resActiveData, setResActiveData] = useState([]);
    const [careOriginData, setCareOriginData] = useState([]);
    const [careActiveData, setCareActiveData] = useState([]);
    const [areaData, setAreaData] = useState([]);
    const [areaShiftData, setAreaShiftData] = useState([]);
    const [reasonData, setReasonData] = useState([]);
    const [artChartData, setARTChartData] = useState([]);
    const [statData, setStatData] = useState([]);
    const [careLvlData, setCareLvlData] = useState([]);
    const [areaTimer, setAreaTimer] = useState(100);
    // LineTrendingVariables
    const [lineData, setLineData] = useState([]);
    const [lineColors, setLineColors] = useState([]);
    const [lineLabels, setLineLabels] = useState([]);
    const [lineAxisLabels, setLineAxisLabels] = useState([]);
    // Table Variables
    const [resRows, setResRows] = useState([]);
    const [careRows, setCareRows] = useState([]);
    const [areaRows, setAreaRows] = useState([]);
    const [areaShiftRows, setAreaShiftRows] = useState([]);
    const [careLow, setCareLow] = useState(.5);
    const [careMed, setCareMed] = useState(1);
    const [careHigh, setCareHigh] = useState(2);
    // General page variables
    const [resLoaded, setResLoaded] = useState(false);
    const [areaLoaded, setAreaLoaded] = useState(false);
    const [areaShiftLoaded, setAreaShiftLoaded] = useState(false);
    const [careLoaded, setCareLoaded] = useState(false);
    const [statLoaded, setStatLoaded] = useState(false);
    const [artChartLoaded, setARTChartLoaded] = useState(false);
    const [reasonLoaded, setReasonLoaded] = useState(false);
    const [count, setCount] = useState(0);
    const [campusId, setCampusId] = useState(dData.acnt);
    const [dayLegend, setDayLegend] = useState(<>
        <div className={"d-inline-block align-middle"}
             style={{width: "1rem", height: "1rem", backgroundColor: "#27B4FFFF", borderRadius: "4px"}}></div>
        <p className={"align-top ms-2 me-5"}>Day</p>
    </>);
    const [eveningLegend, setEveningLegend] = useState(<>
        <div className={"d-inline-block align-middle"}
             style={{width: "1rem", height: "1rem", backgroundColor: "#276BFFFF", borderRadius: "4px"}}></div>
        <p className={"align-top ms-2 me-5"}>Evening</p>
    </>)
    const [nightLegend, setNightLegend] = useState(<>
        <div className={"d-inline-block align-middle"}
             style={{width: "1rem", height: "1rem", backgroundColor: "#0B16FFFF", borderRadius: "4px"}}></div>
        <p className={"align-top ms-2 me-5"}>Night</p>
    </>)
    // Filter page variables
    const [areaTypeOriginData, setAreaTypeOriginData] = useState([]);
    const [areaTypeTableRows, setAreaTypeTableRows] = useState([]);
    const [areaTypeDataCnt, setAreaTypeDataCnt] = useState(0);
    const [areaTypeIdList, setAreaTypeIdList] = useState([]);
    const [areaTypeSearch, setAreaTypeSearch] = useState("");
    const [areaTypeSorter, setAreaTypeSorter] = useState({
        sortColumn: "area_type_name",
        sortOrder: "asc"
    });
    const [areaTypeFilterList, setAreaTypeFilterList] = useState(dData?.dashboard?.main?.areaTypeFilter ? dData.dashboard.main.areaTypeFilter : []);
    const [tabItem, setTabItem] = useState("areaType");
    const [tabCat, setTabCat] = useState("areas");
    const [filterCount,setFilterCount] = useState(0);
    const [resCSVLink, setResCSVLink] = useState({
        filename: "resident_daily_stats.csv",
        headers: [],
        data: []
    });
    const [resCSVButton, setResCSVButton] = useState(
        <CSVLink {...resCSVLink} uFEFF={false}>
            <Button text={"Export"} icon={true} iconClass={"fe fe-download me-2"}
                    class={"btn btn-primary ms-2 px-4 mt-1"}/>
        </CSVLink>);
    const [careCSVLink, setCareCSVLink] = useState({
        filename: "caregiver_daily_stats.csv",
        headers: [],
        data: []
    });
    const [careCSVButton, setCareCSVButton] = useState(
        <CSVLink {...careCSVLink} uFEFF={false}>
            <Button text={"Export"} icon={true} iconClass={"fe fe-download me-2"}
                    class={"btn btn-primary ms-2 px-4 mt-1"}/>
        </CSVLink>);


    resHeaders = [
        {label: "Review Index", key: "review_index_name", align: "center", sorts: false, row: 1, rowSpan: true, rows: 2,
            highlight: {enabled: "is60days", enable_type: "key", useIcon: false, weighted: false, compareKey: "review_index",
                        warnValue: careLow, dangerValue: careMed, extremeValue: careHigh, overunder: "over"}},
        {label: "Resident Name", key: "resident_name", align: "left", sorts: false, row: 1, rowSpan: true, rows: 2,
            link: {enabled: true, type: "standard", linkto: "/profiles/resident", linkkey: "resident_id", sorts: false}},
        {label: "Area", key: "area_name", align: "left", sorts: false, row: 1, rowSpan: true, rows: 2,
            link: {enabled: true, type: "standard", linkto: "/profiles/area", linkkey: "area_id", sorts: false}},
        {label: "Average Daily Events", key: "", row: 1, colSpan: true, cols: 5},
        {label: `Total`, key: "avg_daily_events", align: "center", sorts: false, type: "nullsafe",
            nullValue: 0, nullReplace: "-", row: 2,
            highlight: {enabled: false, useIcon: true, weighted: false, compareKey: "avg_daily_events",
                        warnValue: careLow, dangerValue: careMed, extremeValue: careHigh, overunder: "over"}},
        {label: week1Range,
            key: "week1", align: "center", sorts: false, type: "nullsafe", nullValue: 0, nullReplace: "-", row: 2,
            highlight: {enabled: false, useIcon: true, weighted: false, compareKey: "week1",
                        warnValue: careLow, dangerValue: careMed, extremeValue: careHigh, overunder: "over"}},
        {label: week2Range,
            key: "week2", align: "center", sorts: false, type: "nullsafe", nullValue: 0, nullReplace: "-", row: 2,
            highlight: {enabled: false, useIcon: true, weighted: false, compareKey: "week2",
                        warnValue: careLow, dangerValue: careMed, extremeValue: careHigh, overunder: "over"}},
        {label: week3Range,
            key: "week3", align: "center", sorts: false, type: "nullsafe", nullValue: 0, nullReplace: "-", row: 2,
            highlight: {enabled: false, useIcon: true, weighted: false, compareKey: "week3",
                        warnValue: careLow, dangerValue: careMed, extremeValue: careHigh, overunder: "over"}},
        {label: week4Range,
            key: "week4", align: "center", sorts: false, type: "nullsafe", nullValue: 0, nullReplace: "-", row: 2,
            highlight: {enabled: false, useIcon: true, weighted: false, compareKey: "week4",
                        warnValue: careLow, dangerValue: careMed, extremeValue: careHigh, overunder: "over"}},
        // {label: "Avg Response Rate (Min)", key: "avg_response_time", align: "center", sorts: false, rowSpan: true, row: 2}
    ];

    resCSVHeaders = [
        {label: "Review Index", key: "review_index_name"},
        {label: "Resident", key: "resident_name"},
        {label: "Area", key: "area_name"},
        {label: "Average Daily Events", key: "avg_daily_events"},
        {label: `Average Daily ${formatDate(getToday(-8))}-${formatDate(getToday(-1))}`, key: "week1"},
        {label: `Average Daily ${formatDate(getToday(-14))}-${formatDate(getToday(-8))}`, key: "week2"},
        {label: `Average Daily ${formatDate(getToday(-21))}-${formatDate(getToday(-14))}`, key: "week3"},
        {label: `Average Daily ${formatDate(getToday(-28))}-${formatDate(getToday(-21))}`, key: "week4"},
        {label: "EHR ID", key: "ehr_resident_id"},
        {label: "Level of Care", key: "ehr_level_of_care"},
    ];
    useEffect(() => {
        let interval;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        if (count <= 0){
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardresidentsummaryV2",
                                start_date: startDate, end_date: endDate, active_filter: true,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setResOriginData(data);
                setResLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardcaregiversummaryV2",
                                start_date: startDate, end_date: endDate,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setCareOriginData(data);
                setCareLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardareasummaryV2",
                                start_date: startDate, end_date: endDate,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setAreaData(data);
                setAreaLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardareashiftsummary",
                                start_date: startDate, end_date: endDate,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setAreaShiftData(data);
                setAreaShiftLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardstatsV2",
                                start_date: startDate, end_date: endDate,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setStatData(data?.length > 0 ? data[0] : []);
                setStatLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardreasons",
                                start_date: startDate, end_date: endDate,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setReasonData(data);
                setReasonLoaded(true);
            });
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "dashboardhourchartV2",
                                start_date: startDate, end_date: endDate,
                                campus_id: campusId, area_type_filter: areaTypeFilterList.join(","),
                                device_category_filter: "1,2,15,16"}).then(data => {
                setARTChartData(data);
                setARTChartLoaded(true);
            });
            setCount(count => 600);
        }
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        let interval;
        interval = setInterval(() => {
             setFilterCount(filterCount => filterCount + 1);
        }, 1000);
        if (filterCount <= 0){
            ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "getportalfilters",
                                campus_id: campusId, area_type_filter: "1"}).then(data => {
                setAreaTypeOriginData(() => data?.area_types?.length > 0 ? data.area_types : []);
            });
            setFilterCount(filterCount => 600);
        }
        return () => clearInterval(interval);
    }, [filterCount]);

    useEffect(() => {
        if (dData?.ehr_popup){
            showModal("taskmodal")
        }
    }, []);

    useEffect(() => {
        let toSet = [];
        if (resOriginData !== undefined && resLoaded){
            setResActiveData(resOriginData?.records?.slice(0, 10));
            toSet = resOriginData?.records ? resOriginData.records.slice(0,-1) : [];
            if (resOriginData?.totals?.length > 0){
                toSet.push(resOriginData.totals[0]);
            }
        }
        setResCSVLink(prevState => {
            return {...prevState,
                headers: resCSVHeaders,
                data: toSet
            }
        });
    }, [resOriginData, resLoaded]);

    useEffect(() => {
        setResCSVButton(
            <CSVLink {...resCSVLink} uFEFF={false}>
                <Button text={"Export"} icon={true} iconClass={"fe fe-download me-2"}
                        class={"btn btn-primary ms-2 px-4 mt-1"}/>
            </CSVLink>
        );
    }, [resCSVLink]);

    useEffect(() => {
        let toSet = []
        if (careOriginData !== undefined && careLoaded){
            setCareActiveData(careOriginData?.records?.slice(0, 10))
            toSet = careOriginData?.records ? careOriginData.records.slice(0,-1) : []
            if (careOriginData?.totals?.length > 0){
                toSet.push(careOriginData.totals[0]);
            }
        }
        setCareCSVLink(prevState => {
            return {...prevState,
                headers: careCSVHeaders,
                data: toSet}
        });
    }, [careOriginData, careLoaded]);

    useEffect(() => {
        setCareCSVButton(
            <CSVLink {...careCSVLink} uFEFF={false}>
                <Button text={"Export"} icon={true} iconClass={"fe fe-download me-2"}
                        class={"btn btn-primary ms-2 px-4 mt-1"}/>
            </CSVLink>
        );
    }, [careCSVLink]);

    useEffect(() => {
        let i, tmpData = [], input;
        input = areaTypeSearch?.toString()?.toLowerCase();
        for (i = 0; i < areaTypeOriginData?.length; i++){
            if (((areaTypeOriginData[i]?.area_type_name && areaTypeOriginData[i]?.area_type_name?.toLowerCase()?.search(input) !== -1) ||
                (areaTypeOriginData[i]?.area_type_description && areaTypeOriginData[i]?.area_type_description?.toLowerCase()?.search(input) !== -1))
                && areaTypeOriginData[i]?.area_type_id && showAreaTypes.includes(areaTypeOriginData[i].area_type_id)){
                tmpData.push(areaTypeOriginData[i]);
            }
        }
        setAreaTypeDataCnt(tmpData?.length ? tmpData.length : 0);
        tmpData.sort((a, b) => sortFunc(a, b, areaTypeSorter.sortColumn, areaTypeSorter.sortOrder));
        setAreaTypeTableRows(<TableBody start={0} end={tmpData.length} tableData={tmpData} checkbox={true} writeAccess={true}
                                        actionAppearance={() => {}} tableHeaders={areaTypeHeaders} checkboxId={"uCheckAreaType"}
                                        parentComponent={"EventReports"} dData={dData} checkboxlist={areaTypeIdList}
                                        modal={false} editModal={() => {}} unk={"area-type-x-"}
                                        setCheckboxId={(value) => {setAreaTypeIdList(value)}} checkidkey={"area_type_id"} />);
    }, [areaTypeOriginData, areaTypeSorter, areaTypeIdList, areaTypeSearch]);

    useEffect(() => {
        if (reasonData !== undefined && reasonLoaded && reasonData?.values?.length > 0){
            const bcolor = [];
            for (let ix=0; ix <= reasonData?.values?.length; ix++) {
                bcolor.push(colorlist[ix]);
            }
            if (Chart.getChart("perreason") !== undefined){
                let curChart3 = Chart.getChart("perreason");
                curChart3.data.datasets[0].data = reasonData?.values ? reasonData?.values : [];
                curChart3.data.labels = reasonData?.labels ? reasonData.labels : [];
                curChart3.data.datasets[0].backgroundColor = bcolor;
                curChart3.update();
            }
            else{
                new Chart('perreason', {
                    type: 'pie',
                    options: {
                        responsive: false,
                        cutout: "0%",
                        plugins: {
                            tooltip: {
                                callbacks: {
                                    label: function(tooltipItem){
                                        return ` ${tooltipItem?.label}: ${tooltipItem.formattedValue}%`
                                    }
                                }
                            },
                            legend: {
                                display: true,
                                position: "left"
                            },

                        }
                    },
                    data: {
                        labels: reasonData?.labels ? reasonData.labels : [],
                        datasets: [{
                            data: reasonData?.values ? reasonData?.values : [],
                            backgroundColor: bcolor
                        }]
                    }
                });
            }
        }
    }, [reasonData]);

    useEffect(() => {
        if (artChartData !== undefined && artChartLoaded && statLoaded){
            let tmpNight = [], tmpDay = [], tmpEven = [];
            if (Chart.getChart("perhour") !== undefined){
                const curChart = Chart.getChart("perhour");
                curChart.data.datasets[0].data = artChartData?.avg_events ? artChartData.avg_events : [];
                curChart.data.datasets[1].data = artChartData?.responses ? artChartData.responses : [];
                curChart.options.plugins.annotation.annotations.line1.value = statData?.avg_response_time ? statData?.avg_response_time : 0;
                curChart.resize();
                curChart.update();
            }
            else{
                new Chart('perhour', {
                    options: {
                        responsive: true,
                        plugins: {
                            annotation: {
                                drawTime: 'afterDatasetsDraw',
                                annotations: {
                                    line1: {
                                        type: 'line',
                                        borderDash: [8, 4],
                                        borderWidth: 4,
                                        scaleID: "b",
                                        borderColor: "#BE4D2578",
                                        value: statData?.avg_response_time ? statData?.avg_response_time : 0,
                                        label: {
                                            display: false,
                                            backgroundColor: "#BE4D25",
                                            borderColor: "#000000",
                                            borderWidth: 2,
                                            content: `Response Time Average: ${statData?.avg_response_time ? statData?.avg_response_time : 0}`,
                                        },
                                        enter({element}, event) {
                                            element.label.options.display = true;
                                            return true;
                                        },
                                        leave({element}, event) {
                                            element.label.options.display = false;
                                            return true;
                                        }
                                    }
                                }
                            },
                            legend: {
                                display: true
                            },
                        },
                        scales: {
                            x: {
                                title: {
                                    display: true,
                                    text: "Time of the Day",
                                },
                                grid: {
                                    display: false
                                },
                                ticks: {
                                    callback: function (value){
                                        return hourToLocalizedHour(value);
                                    }
                                }
                            },
                            a: {
                                id: "a",
                                position: "left",
                                title: {
                                    display: true,
                                    text: "Average Alarms",
                                    color: "#0081C7",
                                    font: {
                                        size: 14,
                                        weight: "600"
                                    }
                                },
                                grid: {
                                    display: false
                                },
                                suggestedMax: 1,
                                suggestedMin: 0,
                                ticks: {
                                    callback: function (value) {
                                        return value
                                    }
                                }
                            },
                            b: {
                                id: "b",
                                position: "right",
                                title: {
                                    display: true,
                                    text: "Average Response Time (min)",
                                    color: "#BE4D25",
                                    font: {
                                        size: 14,
                                        weight: "600"
                                    }
                                },
                                grid: {
                                    display: false
                                },
                                suggestedMax: 1,
                                suggestedMin: 0,
                                ticks: {
                                    callback: function (value) {
                                        return value
                                    }
                                }
                            }
                        }
                    },
                    data: {
                        labels: handleShiftLabels(artChartData?.hours ? artChartData?.hours : []),
                        datasets: [{
                            type: "bar",
                            label: 'Average Alarms/day',
                            data: artChartData?.avg_events ? artChartData.avg_events : [],
                            backgroundColor: handleShiftColors(artChartData?.hours ? artChartData?.hours : []),
                            hoverBackgroundColor: ["#0081C778"],
                            yAxisID: "a",
                            borderRadius: 5,
                            order: 2
                        },{
                            type: "line",
                            label: "Average Response Time",
                            data: artChartData?.responses ? artChartData.responses : [],
                            backgroundColor: ["#BE4D25"],
                            hoverBackgroundColor: ["#BE4D2578"],
                            borderColor: ["#BE4D25"],
                            borderWidth: 3,
                            pointRadius: 5,
                            pointBorderColor: "#ffffff",
                            pointBorderWidth: 1,
                            pointHoverRadius: 9,
                            pointBackgroundColor: "#BE4D25",
                            pointHoverBackgroundColor: "#BE4D25",
                            pointHoverBorderColor: "#ffffff",
                            pointHoverBorderWidth: 2,
                            tension: 0.4,
                            fill: false,
                            yAxisID: "b",
                            order: 1
                        }]
                    }
                });
            }
            for (let i=0; i < artChartData?.hours?.length; i++){
                if (artChartData.hourShift[i.toString()] === "Night"){
                    tmpNight.push(<li key={`night-time-${i}`}>{hourToLocalizedHour(i)}</li>);
                }else if (artChartData.hourShift[i.toString()] === "Day"){
                    tmpDay.push(<li key={`day-time-${i}`}>{hourToLocalizedHour(i)}</li>);
                }else{
                    tmpEven.push(<li key={`evening-time-${i}`}>{hourToLocalizedHour(i)}</li>);
                }
            }
            setNightLegend(<OverlayTrigger trigger={["hover", "focus"]} key={`popover-night-legend`}
                                            placement={"left"}
                                            overlay={
                                                <Popover id={`popover-overlay-night`} className={"popover1"}>
                                                    <Popover.Header as="h3" className={"popover-header1 text-light"}>
                                                        <div className={"d-inline-block align-middle"}
                                                             style={{
                                                                 width: "1rem", height: "1rem",
                                                                 backgroundColor: "#0B16FFFF", borderRadius: "4px"
                                                             }}></div>
                                                        <p className={"align-top ms-2"}>Night Shift</p>
                                                    </Popover.Header>
                                                    <Popover.Body className={"popover-body1"}>
                                                        <ul className={"text-center"}
                                                            style={{"listStyle": "none", padding: "inherit"}}>
                                                            {tmpNight}
                                                        </ul>
                                                    </Popover.Body>
                                                </Popover>
                                            }>
                                    <div className={"d-inline-block"}>
                                        <div className={"d-inline-block align-middle"}
                                             style={{width: "1rem", height: "1rem", backgroundColor: "#0B16FFFF", borderRadius: "4px"}}></div>
                                        <p className={"align-top ms-2 me-5"}>Night</p>
                                    </div>
                            </OverlayTrigger>);
            setDayLegend(<OverlayTrigger trigger={["hover", "focus"]} key={`popover-day-legend`}
                                        placement={"left"}
                                        overlay={
                                            <Popover id={`popover-overlay-day`} className={"popover1"}>
                                                <Popover.Header as="h3" className={"popover-header1 text-light"}>
                                                    <div className={"d-inline-block align-middle"}
                                                         style={{
                                                             width: "1rem", height: "1rem",
                                                             backgroundColor: "#27B4FFFF", borderRadius: "4px"
                                                         }}></div>
                                                    <p className={"align-top ms-2"}>Day Shift</p>
                                                </Popover.Header>
                                                <Popover.Body className={"popover-body1"}>
                                                    <ul className={"text-center"}
                                                        style={{"listStyle": "none", padding: "inherit"}}>
                                                        {tmpDay}
                                                    </ul>
                                                </Popover.Body>
                                            </Popover>
                                        }>
                                        <div className={"d-inline-block"}>
                                            <div className={"d-inline-block align-middle"}
                                                 style={{
                                                     width: "1rem",
                                                     height: "1rem",
                                                     backgroundColor: "#27B4FFFF",
                                                     borderRadius: "4px"
                                                 }}></div>
                                            <p className={"align-top ms-2 me-5"} style={{cursor: "default"}}>Day</p>
                                        </div>
                            </OverlayTrigger>);
            setEveningLegend(<OverlayTrigger trigger={["hover", "focus"]} key={`popover-evening-legend`}
                                             placement={"left"}
                                            overlay={
                                                <Popover id={`popover-overlay-evening`} className={"popover1"}>
                                                    <Popover.Header as="h3" className={"popover-header1 text-light"}>
                                                        <div className={"d-inline-block align-middle"}
                                                             style={{
                                                                 width: "1rem", height: "1rem",
                                                                 backgroundColor: "#276BFFFF", borderRadius: "4px"
                                                             }}></div>
                                                        <p className={"align-top ms-2"}>Evening Shift</p>
                                                    </Popover.Header>
                                                    <Popover.Body className={"popover-body1"}>
                                                        <ul className={"text-center"}
                                                            style={{"listStyle": "none", padding: "inherit"}}>
                                                            {tmpEven}
                                                        </ul>
                                                    </Popover.Body>
                                                </Popover>
                                            }>
                                    <div className={"d-inline-block"}>
                                        <div className={"d-inline-block align-middle"}
                                             style={{width: "1rem", height: "1rem", backgroundColor: "#276BFFFF", borderRadius: "4px"}}></div>
                                        <p className={"align-top ms-2 me-5"}>Evening</p>
                                    </div>
            </OverlayTrigger>)
        }
    }, [artChartData, statData, artChartLoaded, statLoaded]);

    useEffect(() => {
        if (areaData && areaData?.records){
            setLineColors(areaData.records.map((item, index) => {return colorlist[index]}));
            setLineLabels(areaData.records.map((item) => {return item.area_type_name}));
            setLineData(areaData.records.map((item) => {return unpackFieldsToArray(item, ["week4", "week3", "week2", "week1"])}));
            setLineAxisLabels([week4Range, week3Range, week2Range, week1Range]);
        }
    }, [areaData]);

    useEffect(() => {
        if (resActiveData !== undefined && resLoaded){
            setResRows(() => {
                return (<TableBody start={0} end={resActiveData?.length ? resActiveData.length : 0}
                                   tableData={resActiveData ? resActiveData : []}
                                   checkbox={false} writeAccess={false}
                                   actionAppearance={() => {}} tableHeaders={resHeaders} checkboxId={""}
                                   parentComponent={"MainDash"} dData={dData} checkboxlist={[]}
                                   modal={false} editModal={() => {}} unk={"rs-"} setCheckboxId={() => {}} checkidkey={""} />)
            })
        }
    }, [resActiveData]);

    useEffect(() => {
        if (careActiveData !== undefined && careLoaded){
            setCareRows(() => {
                return (<TableBody start={0} end={careActiveData?.length ? careActiveData.length : 0}
                                   tableData={careActiveData ? careActiveData : []}
                                   checkbox={false} writeAccess={false}
                                   actionAppearance={() => {}} tableHeaders={careHeaders} checkboxId={""}
                                   parentComponent={"MainDash"} dData={dData} checkboxlist={[]}
                                   modal={false} editModal={() => {}} unk={"cs-"} setCheckboxId={() => {}} checkidkey={""} />);
            });
        }
    }, [careActiveData]);

    useEffect(() => {
        if (areaData !== undefined){
            setAreaRows(() => {
                return (<TableBody start={0} end={areaData?.records?.length ? areaData.records.length : 0}
                                   tableData={areaData?.records ? areaData.records : []}
                                   checkbox={false} writeAccess={false}
                                   actionAppearance={() => {}} tableHeaders={areaHeaders} checkboxId={""}
                                   parentComponent={"MainDash"} dData={dData} checkboxlist={[]}
                                   modal={false} editModal={() => {}} unk={"ar-"} setCheckboxId={() => {}} checkidkey={""} />);
            });
        }
    }, [areaData]);

    useEffect(() => {
        if (areaShiftData !== undefined){
            setAreaShiftRows(() => {
                return (<TableBody start={0} end={areaShiftData?.records?.length ? areaShiftData.records.length : 0}
                                   tableData={areaShiftData?.records ? areaShiftData.records : []}
                                   checkbox={false} writeAccess={false}
                                   actionAppearance={() => {}} tableHeaders={areaShiftHeaders} checkboxId={""}
                                   parentComponent={"MainDash"} dData={dData} checkboxlist={[]}
                                   modal={false} editModal={() => {}} unk={"as-"} setCheckboxId={() => {}} checkidkey={""} />);
            });
        }
    }, [areaShiftData]);

    function clearFilters(){
        // Function used in filter modal to clear all toggled checkboxes across all tabs.
        setAreaTypeSearch("");
        setAreaTypeIdList([]);
        document.getElementById("uCheckHead2").checked = false;
    }

    function showFilterModal(){
        // Function that preps and populates the filter modal with the checks when called based on stored session.
        setAreaTypeSearch("");
        setAreaTypeIdList(areaTypeFilterList);
        document.getElementById("uCheckHead2").checked = areaTypeFilterList?.length === areaTypeOriginData?.length;
        showModal("filtermodal2");
    }

    function filterSubmit2(){
        // Function used to set filters to vars on submit from filter modal.
        handleSubmitActions("filterSubmit2", "loadFilterSubmit2");
        setAreaTypeFilterList(areaTypeIdList);
        if (dData?.dashboard === undefined){
            dData["dashboard"] = {}
        }
        if (dData.dashboard?.main === undefined){
            dData["dashboard"]["main"] = {}
        }
        dData["dashboard"]["main"]["areaTypeFilter"] = areaTypeIdList;
        setSession(dData);
        setCount(() => 0);
        formSuccess("Filters saved", "filterSubmit2", "loadFilterSubmit2", "cancelFilterBtn2");
    }

    function filtersOn(){
        return [areaTypeFilterList].some(filterArray => filterArray?.length > 0);
    }

    function handleShiftColors(hours){
        let ret = [];
        for (let i=0; i < hours?.length; i++){
            ret.push(artChartData?.shiftColor[artChartData.hourShift[hours[i].toString()]]);
        }
        return ret;
    }

    function handleShiftLabels(hours){
        let ret = [];
        for (let i=0; i < hours?.length; i++){
            ret.push(`${artChartData?.hourShift[hours[i].toString()]}\n${hourToLocalizedHour(hours[i])}`);
        }
        return ret;
    }

    const headerContent = (
        <div className={"d-flex justify-content-between"}>
            <div>
                <button type={"button"} className={"btn btn-secondary mx-2 mb-2"} style={{height: "46.5px"}}
                        onClick={() => {
                            showFilterModal();
                        }}>
                    <i className={"fe fe-filter fs-3"}/>
                    {filtersOn() && <div className={"filter-caret"}></div>}
                </button>
            </div>
            <CampusDropDown campusId={campusId} setCampusId={(item) => setCampusId(item)} setCount={(item) => {
                setARTChartLoaded(false);
                setCount(item);
                setFilterCount(item);
            }} pageAction={() => {
            }}/>
        </div>
    );

    const resTableHead = <TableHeaders checkbox={false} headerData={resHeaders} sorts={false} sortOrder={{}}
                                       sortColumn={{}} sorter={{}} setSorter={() => {}} rowCnt={2} />

    const careTableHead = <TableHeaders checkbox={false} headerData={careHeaders} sorts={false} sortOrder={{}}
                                        sortColumn={{}} sorter={{}} setSorter={() => {}} rowCnt={2} />

    const areaTableHead = <TableHeaders checkbox={false} headerData={areaHeaders} sorts={false} sortOrder={{}}
                                        sortColumn={{}} sorter={{}} setSorter={() => {}} />

    const areaShiftTableHead = <TableHeaders checkbox={false} headerData={areaShiftHeaders} sorts={false} sortOrder={{}}
                                             sortColumn={{}} sorter={{}} setSorter={() => {}} rowCnt={2} />

    const areaTypeTableHeaders = <TableHeaders checkbox={true} headerData={areaTypeHeaders} checkboxlist={areaTypeIdList}
                                               checkAll={() => checkAll(0, areaTypeDataCnt ? areaTypeDataCnt : 0, areaTypeIdList, setAreaTypeIdList, "uCheckHead2", "uCheckAreaType")}
                                               writeAccess={true} sortOrder={areaTypeSorter.sortOrder} checkboxHeaderId={"uCheckHead2"}
                                               sortColumn={areaTypeSorter.sortColumn} sorter={areaTypeSorter} setSorter={setAreaTypeSorter}
                                               actionAppearance={() => {}} />

    return (
        <>
            <Navigation/>
            <div className={"main-content"}>
                <Header preTitle={"Dashboard"} Title={"Main"} content={headerContent}/>
                <div className={"container-fluid"}>
                    <div className={"row"}>
                        <div className={"col-lg-4 col-xl"}>
                            <div className={"card"}>
                                <div className={"card-body"}>
                                    <div className={"row align-items-center gx-0"}>
                                        <div className={"col"}>
                                            <h6 className={"text-uppercase text-muted mb-2"}>Avg Daily Alarms</h6>
                                            {statLoaded
                                                ?
                                                <span
                                                    className={"h2 mb-0"}>{statData?.avg_daily_events ? statData.avg_daily_events : 0}</span>
                                                :
                                                <Button type={"button"} text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}/>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={"col-lg-4 col-xl"}>
                            <div className={"card"}>
                                <div className={"card-body"}>
                                    <div className={"row align-items-center gx-0"}>
                                        <div className={"col"}>
                                            <h6 className={"text-uppercase text-muted mb-2"}>Avg Response Rate</h6>
                                            {statLoaded
                                                ?
                                                <span className={"h2 mb-0"}>{statData?.avg_response_time ? statData.avg_response_time : 0.0} min</span>
                                                :
                                                <Button type={"button"} text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}/>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={"col-lg-4 col-xl"}>
                            <div className={"card"}>
                                <div className={"card-body"}>
                                    <div className={"row align-items-center gx-0"}>
                                        <div className={"col"}>
                                            <h6 className={"text-uppercase text-muted mb-2"}>Alarms Over 20 Min</h6>
                                            {statLoaded
                                                ?
                                                <span
                                                    className={"h2 mb-0"}>{statData?.percent_events_over_20 ? statData.percent_events_over_20 : 0.0}%</span>
                                                :
                                                <Button type={"button"} text={""} class={"border-0 bg-transparent btn"}
                                                        spinner={true}/>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-xl-6"}>
                            <div className={"card"}>
                                <div className={"card-header"}>
                                    <h4 className={"card-header-title"}>Top 10 Resident Review</h4>
                                    {resCSVButton}
                                </div>
                                <div className={"card-body"} style={{overflowX: "auto"}}>
                                    {resLoaded
                                        ?
                                        (<>
                                            <table className={"table table-sm table-hover w-100"}>
                                                <thead style={{height: "97px"}}>
                                                {resTableHead}
                                                </thead>
                                                <tbody>
                                                {resRows}
                                                {resOriginData?.totals
                                                    ?
                                                    resOriginData.totals.map((record) => {
                                                        return (
                                                            <tr key={"resident-total-row"}>
                                                                <td className={"px-1 text-start fw-bold border-light border-top-2"}>{record?.resident_name ? record.resident_name : "Totals"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}></td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}></td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_daily_events ? record.avg_daily_events.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week1 ? record.week1.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week2 ? record.week2.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week3 ? record.week3.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week4 ? record.week4.toLocaleString() : "0"}</td>
                                                            </tr>
                                                        )
                                                    })
                                                    :
                                                    <></>
                                                }
                                                </tbody>
                                            </table>
                                            <div>
                                                <div className={"d-inline-block me-3"}>
                                                    <MaterialIcon size={"small"}
                                                                  className={"material-icons ps-2 md-18 text-warning align-text-bottom d-inline-block"}
                                                                  icon={"radio_button_checked"}/>
                                                    <h3 className={"d-inline-block ms-2 mb-1"}>Low</h3>
                                                    <h5 className={"d-block ms-2 text-center fst-italic"}>*More
                                                        than {careLow}</h5>
                                                </div>
                                                <div className={"d-inline-block me-3"}>
                                                    <MaterialIcon size={"small"}
                                                                  className={"material-icons ps-2 md-18 text-risk align-text-bottom d-inline-block"}
                                                                  icon={"radio_button_checked"}/>
                                                    <h3 className={"d-inline-block ms-2 mb-1"}>Medium</h3>
                                                    <h5 className={"d-block ms-2 text-center fst-italic"}>*More
                                                        than {careMed}</h5>
                                                </div>
                                                <div className={"d-inline-block me-3"}>
                                                    <MaterialIcon size={"small"}
                                                                  className={"material-icons ps-2 md-18 text-danger align-text-bottom d-inline-block"}
                                                                  icon={"radio_button_checked"}/>
                                                    <h3 className={"d-inline-block ms-2 mb-1"}>High</h3>
                                                    <h5 className={"d-block ms-2 text-center fst-italic"}>*More
                                                        than {careHigh}</h5>
                                                </div>
                                            </div>
                                        </>)
                                        :
                                        (<div className={"text-center"}>
                                            <Button text={""} class={"border-0 bg-transparent btn"}
                                                    spinner={true}></Button>
                                        </div>)}
                                </div>
                            </div>
                        </div>
                        <div className={"col-xl-6"}>
                            <div className={"card"}>
                                <div className={"card-header"}>
                                    <h4 className={"card-header-title"}>Top 10 Caregiver Review</h4>
                                    {careCSVButton}
                                </div>
                                <div className={"card-body"} style={{overflowX: "auto"}}>
                                    {careLoaded
                                        ?
                                        (<>
                                            <table className={"table table-sm w-100"}>
                                                <thead style={{height: "97px"}}>
                                                {careTableHead}
                                                </thead>
                                                <tbody>
                                                {careRows}
                                                {careOriginData?.totals
                                                    ?
                                                    careOriginData.totals.map((record) => {
                                                        return (
                                                            <tr key={"caregiver-total-row"}>
                                                                <td className={"px-1 text-start fw-bold border-light border-top-2"}>Totals</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_daily_events ? record.avg_daily_events.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week1 ? record.week1.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week2 ? record.week2.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week3 ? record.week3.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.week4 ? record.week4.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_response_time ? record.avg_response_time.toLocaleString() : "0"}</td>
                                                            </tr>
                                                        )
                                                    })
                                                    :
                                                    <></>
                                                }
                                                </tbody>
                                            </table>
                                        </>)
                                        :
                                        (<div className={"text-center"}>
                                            <Button text={""} class={"border-0 bg-transparent btn"}
                                                    spinner={true}></Button>
                                        </div>)
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-xl-6"}>
                            <div className={"card"}>
                                <div className={"card-header"}>
                                    <h4 className={"card-header-title"}>Response Time Trends</h4>
                                </div>
                                <div className={"card-body"} style={{overflowX: "auto"}}>
                                    {areaLoaded
                                        ?
                                        <div className={"chart-container"}>
                                            <LineTrendingChart
                                                style={{maxHeight: "450px"}}
                                                x_axis={true}
                                                x_axis_title={"Weeks"}
                                                line_axis={true}
                                                line_axis_title={"Response Time (Min)"}
                                                line_color={lineColors}
                                                lines={areaData?.records?.length ? areaData.records.length : 1}
                                                line_data={lineData}
                                                line_labels={lineLabels}
                                                forceAspect={true}
                                                labels={lineAxisLabels} />
                                        </div>
                                        :
                                        (<div className={"text-center"}>
                                            <Button text={""} class={"border-0 bg-transparent btn"}
                                                    spinner={true}></Button>
                                        </div>)
                                    }
                                </div>
                            </div>
                        </div>
                        <div className={"col-xl-6"}>
                            <div className={"card"}>
                                <div className={"card-header"}>
                                    <h4 className={"card-header-title"}>Alarms by Reason</h4>
                                </div>
                                <div className={"card-body text-center"}>
                                    <div className={"card-body text-center"}>
                                        {!reasonLoaded ? (
                                            <div className={"text-center"}>
                                                <Button text={""} class={"border-0 bg-transparent btn"} spinner={true}/>
                                            </div>
                                        ) : reasonData?.values?.length > 0 ? (
                                            <div className={"chart-container"}>
                                                <canvas className={"chart-canvas d-inline-block"}
                                                        style={{width: "400px", height: "400px"}} id={"perreason"}/>
                                            </div>
                                        ) : (
                                            <div className={"text-center"}>
                                                <h4>Reasons not currently being used</h4>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-xl-12"}>
                            <div className={"card"}>
                                <div className={"card-header"}>
                                    <h4 className={"card-header-title"}>Areas by Shift</h4>
                                </div>
                                <div className={"card-body"} style={{overflowX: "auto"}}>
                                    {areaShiftLoaded
                                        ?
                                        (<>
                                            <table className={"table table-sm w-100"}>
                                                <thead>
                                                {areaShiftTableHead}
                                                </thead>
                                                <tbody>
                                                {areaShiftRows}
                                                {areaShiftData?.totals
                                                    ?
                                                    areaShiftData.totals?.map((record) => {
                                                        return (
                                                            <tr key={"area-total-row"}>
                                                                <td className={"px-1 text-start fw-bold border-light border-top-2"}>{record?.area_type_name}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_daily_events_1st ? record.avg_daily_events_1st.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_response_time_1st ? record.avg_response_time_1st.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_daily_events_2nd ? record.avg_daily_events_2nd.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_response_time_2nd ? record.avg_response_time_2nd.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_daily_events_3rd ? record.avg_daily_events_3rd.toLocaleString() : "0"}</td>
                                                                <td className={"px-1 text-center fw-bold border-light border-top-2"}>{record?.avg_response_time_3rd ? record.avg_response_time_3rd.toLocaleString() : "0"}</td>
                                                            </tr>
                                                        )
                                                    })
                                                    :
                                                    <></>
                                                }
                                                </tbody>
                                            </table>
                                        </>)
                                        :
                                        (<div className={"text-center"}>
                                            <Button text={""} class={"border-0 bg-transparent btn"}
                                                    spinner={true}></Button>
                                        </div>)
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"row"} style={{width: "calc(100%)", marginLeft: 0}}>
                        <div className={"card"}>
                            <div className={"card-header"}>
                                <h4 className={"card-header-title"}>Average Alarms and Response Times (min) by Hour</h4>
                                <div className={"pt-3"}>
                                    {dayLegend}
                                    {eveningLegend}
                                    {nightLegend}
                                </div>
                            </div>
                            <div className={"card-body"}>
                                {artChartLoaded
                                    ?
                                    (<div className={"chart-container"}>
                                        <canvas className={"chart-canvas p-1"} style={{maxHeight: "450px"}}
                                                id={"perhour"}></canvas>
                                    </div>)
                                    :
                                    (<div className={"text-center"}>
                                        <Button text={""} class={"border-0 bg-transparent btn"} spinner={true}></Button>
                                    </div>)
                                }
                            </div>
                        </div>
                    </div>
                    {dData?.campusData[dData?.acnt?.toString()]?.resident_trends && <ResidentTrendActions campus_id={campusId} />}
                    <div className={"footer-spacer"}>

                    </div>
                </div>
                <div className={"modal fade"} id={"taskmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                     tabIndex={-1} aria-labelledby={"staticBackdropLabel"} aria-hidden={"true"}>
                    <div className={"modal-dialog"}>
                        <div className={"modal-content"}>
                            <div className={"modal-header bg-pal"}>
                                <button type={"button"} className={"btn-close"} id={"filterClose2"}
                                        data-bs-dismiss={"modal"} aria-label={"Close"}/>
                            </div>
                            <div className={"modal-body"}>
                                <h2 className={"text-center"}>You
                                    have {getEhrInfo().stg_count || 0} items to approve from
                                    your {getEhrInfo().vendor_name} integration</h2>
                                <div className={"text-center my-4"}>
                                    <button type={"button"} className={"btn btn-primary mx-4 fs-3"} id={"approvenowbtn"}
                                            onClick={() => {
                                                dData["ehr_popup"] = false;
                                                setSession(dData);
                                                window.location.href = "/tasks/ehr";
                                            }}>Approve Now
                                    </button>
                                    <button type={"button"} className={"btn btn-outline-secondary mx-4 fs-3"}
                                            id={"approvelaterbtn"} data-bs-dismiss={"modal"}
                                            onClick={() => {
                                                dData["ehr_popup"] = false;
                                                setSession(dData);
                                            }}>Approve Later
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"modal fade"} id={"filtermodal2"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                     tabIndex={-1} aria-labelledby={"staticBackdropLabel"} aria-hidden={"true"}>
                    <div className={"modal-dialog"}>
                        <div className={"modal-content"}>
                            <div className={"modal-header bg-pal"}>
                                <h2 className={"modal-title"} id={"staticBackdropLabel"}>Filter Settings</h2>
                                <Button text={"Clear All"} class={"btn ms-3 pb-0 hover-text-white"}
                                        onClick={() => clearFilters()}/>
                                <button type={"button"} className={"btn-close"} id={"filterClose2"}
                                        data-bs-dismiss={"modal"} aria-label={"Close"}/>
                            </div>
                            <form id={"filterForm2"}>
                                <div className={"modal-body pt-3"}>
                                    <div className={"text-muted text-center mb-2"}>*Filters set for the main dashboard will persist until cleared.</div>
                                    <div className={"row align-items-center"}
                                         style={{borderBottom: "dotted 1px #b7b7b7"}}>
                                        {/*<div className={"col"}>*/}
                                        {/*    <ul className={"nav nav-tabs nav-overflow header-tabs mb-0"}>*/}
                                        {/*        <li className={"mx-1 my-2 d-flex"}>*/}
                                        {/*            <Button text={"Areas"}*/}
                                        {/*                    class={tabCat === "areas" ? "btn btn-toggle btn-active-primary" : "btn btn-toggle"}*/}
                                        {/*                    style={{width: "95px"}}*/}
                                        {/*                    onClick={() => {*/}
                                        {/*                        setTabCat("areas");*/}
                                        {/*                        setTabItem("areaType");*/}
                                        {/*                    }}/>*/}
                                        {/*        </li>*/}
                                        {/*    </ul>*/}
                                        {/*</div>*/}
                                    </div>
                                    <div className={"mt-2"}>
                                        <div>
                                            {tabCat === "areas"
                                                ? <ul className={"nav nav-tabs nav-overflow header-tabs"}>
                                                    <li className={"nav-item"}>
                                                        <Button text={"Area Types"}
                                                                class={tabItem === "areaType" ? "nav-link active" : "nav-link"}
                                                                onClick={() => setTabItem("areaType")}/>
                                                    </li>
                                                </ul>
                                                : <></>
                                            }
                                        </div>
                                        <div className={"form-outline d-inline-flex align-bottom ms-2 my-3"}>
                                            <input type={"search"} id={"areaTypeSearch"}
                                                   className={"form-control search-input"}
                                                   value={areaTypeSearch}
                                                   onChange={(e) => setAreaTypeSearch(e.target?.value)}/>
                                            <Button text={""} type={"button"}
                                                    class={"btn btn-primary smooth-radius-left"}
                                                    style={{height: "42px"}} icon={true} iconClass={"fe fe-search"}/>
                                        </div>
                                        <table className={"table table-sm table-white table-hover"}
                                               style={{maxHeight: "400px", overflowY: "auto"}}>
                                            <thead className={"sticky-table-head"} style={{top: "0px"}}>
                                            {areaTypeTableHeaders}
                                            </thead>
                                            <tbody>
                                            {areaTypeTableRows}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className={"modal-footer"}>
                                    <div>
                                        <button type={"button"} className={"btn btn-secondary"} id={"cancelFilterBtn2"}
                                                data-bs-dismiss={"modal"}>Cancel
                                        </button>
                                        <Button type={"button"} class={"btn btn-primary d-none ms-3"} disabled={true}
                                                spinner={true} text={"Loading"} id={"loadFilterSubmit2"}/>
                                        <button type={"button"} className={"btn btn-primary ms-3"} id={"filterSubmit2"}
                                                onClick={() => filterSubmit2()}>Submit
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <Suspense fallback={"Loading..."}>
                    <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                    autoClose={10000} closeOnClick={true}/>
                </Suspense>
                <button
                    type={"button"}
                    className={"help-corner-button"}
                    onClick={() => showModal("helpmodal")}
                    title={"Page Help Popup"}
                    aria-label={"Open Help"}
                >
                    <i className={"fe fe-help-circle help-corner-button-icon"}></i>
                </button>
                <div className={"modal fade help-modal-pop"} id={"helpmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                     tabIndex={-1} aria-labelledby={"staticHelpBackdropLabel"} aria-hidden={"true"}>
                    <div className={"modal-dialog modal-lg"}>
                        <div className={"modal-content"}>
                            <div className={"modal-header bg-pal"}>
                                <h2 className={"modal-title"} id={"staticHelpBackdropLabel"}>Main Dashboard Help</h2>
                                <button type={"button"} className={"btn-close"} data-bs-dismiss={"modal"} aria-label={"Close"}/>
                            </div>
                            <div className={"modal-body"}>
                                <div className={"mb-4"}>
                                    <h4 className={"text-primary mb-3"}>
                                        <i className={"fe fe-info me-2"}></i>Getting Started
                                    </h4>
                                    <p className={"mb-2"}>
                                        The Main Dashboard provides a comprehensive overview of your campus's alarm activity, response times, and performance metrics. 
                                        This page displays key statistics, trends, and top performers across residents, caregivers, and areas over the last 28 days.
                                    </p>
                                    <p className={"mb-2"}>
                                        <strong>Note:</strong> All statistics are based on 28 days of data, including only pendant and push/pull cord alerts under 60 minutes, and excluding common areas.
                                    </p>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-bar-chart-2 me-2 text-primary"}></i>Summary Statistics
                                    </h5>
                                    <p className={"mb-2"}>The top row displays three key performance indicators:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}><strong>Avg Daily Alarms</strong>: The average number of alarms per day over the last 28 days</li>
                                        <li className={"mb-2"}><strong>Avg Response Rate</strong>: The average response time in minutes for all alarms</li>
                                        <li className={"mb-2"}><strong>Alarms Over 20 Min</strong>: The percentage of alarms that took longer than 20 minutes to respond to</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-users me-2 text-primary"}></i>Top 10 Resident Review
                                    </h5>
                                    <p className={"mb-2"}>This table shows the top 10 residents by average daily alarm volume:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}><strong>Review Index</strong>: A color-coded indicator showing alarm volume levels (Low, Medium, High)</li>
                                        <li className={"mb-2"}><strong>Resident Name</strong>: Click to view the resident's profile page</li>
                                        <li className={"mb-2"}><strong>Area</strong>: The area where the resident is located (clickable link to area profile)</li>
                                        <li className={"mb-2"}><strong>Average Daily Events</strong>: Shows total average and breakdown by week (Week 1-4)</li>
                                        <li className={"mb-2"}>Color indicators: <span className={"text-warning"}>Yellow</span> = Low (more than 0.5), <span className={"text-risk"}>Orange</span> = Medium (more than 1), <span className={"text-danger"}>Red</span> = High (more than 2)</li>
                                        <li className={"mb-2"}>Use the <strong>"Export"</strong> button to download the data as a CSV file</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-user-check me-2 text-primary"}></i>Top 10 Caregiver Review
                                    </h5>
                                    <p className={"mb-2"}>This table shows the top 10 caregivers by average daily alarm volume:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}><strong>User Name</strong>: Click to view the caregiver's profile page</li>
                                        <li className={"mb-2"}><strong>Average Daily Events</strong>: Shows total average and breakdown by week (Week 1-4)</li>
                                        <li className={"mb-2"}><strong>Avg Response Rate (Min)</strong>: The average response time in minutes for alarms accepted by this caregiver</li>
                                        <li className={"mb-2"}>Use the <strong>"Export"</strong> button to download the data as a CSV file</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-trending-up me-2 text-primary"}></i>Response Time Trends
                                    </h5>
                                    <p className={"mb-2"}>This chart displays response time trends by area type over the last 28 days:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>Each line represents a different area type</li>
                                        <li className={"mb-2"}>The chart shows weekly trends (Week 1-4) for total alarms</li>
                                        <li className={"mb-2"}>Use this to identify trends and patterns in alarm activity across different area types</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-pie-chart me-2 text-primary"}></i>Alarms by Reason
                                    </h5>
                                    <p className={"mb-2"}>This pie chart shows the distribution of alarms by reason:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>Each slice represents a different alarm reason</li>
                                        <li className={"mb-2"}>Hover over slices to see the percentage and reason name</li>
                                        <li className={"mb-2"}>Use this to identify the most common alarm reasons in your facility</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-clock me-2 text-primary"}></i>Areas by Shift
                                    </h5>
                                    <p className={"mb-2"}>This table shows alarm statistics broken down by area type and shift:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}><strong>Day Shift</strong>: Statistics for the day shift (color-coded in light blue)</li>
                                        <li className={"mb-2"}><strong>Evening Shift</strong>: Statistics for the evening shift (color-coded in medium blue)</li>
                                        <li className={"mb-2"}><strong>Night Shift</strong>: Statistics for the night shift (color-coded in dark blue)</li>
                                        <li className={"mb-2"}>Each shift shows: Average Daily Alarms and Average Response Time (minutes)</li>
                                        <li className={"mb-2"}>Use this to compare performance across different shifts</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-activity me-2 text-primary"}></i>Average Alarms and Response Times by Hour
                                    </h5>
                                    <p className={"mb-2"}>This chart shows alarm activity and response times throughout the day:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}><strong>Bar Chart</strong>: Shows average alarms per day for each hour (color-coded by shift)</li>
                                        <li className={"mb-2"}><strong>Line Chart</strong>: Shows average response time in minutes for each hour</li>
                                        <li className={"mb-2"}><strong>Dashed Line</strong>: Represents the overall average response time (hover to see value)</li>
                                        <li className={"mb-2"}>Shift colors: <span style={{color: "#27B4FFFF"}}>Light Blue</span> = Day, <span style={{color: "#276BFFFF"}}>Medium Blue</span> = Evening, <span style={{color: "#0B16FFFF"}}>Dark Blue</span> = Night</li>
                                        <li className={"mb-2"}>Hover over shift legend items to see which hours belong to each shift</li>
                                        <li className={"mb-2"}>Use this to identify peak alarm times and response patterns throughout the day</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-filter me-2 text-primary"}></i>Filtering Data
                                    </h5>
                                    <p className={"mb-2"}>You can filter the dashboard data by area types:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>Click the <strong>Filter</strong> button (with filter icon) in the header</li>
                                        <li className={"mb-2"}>Select or deselect area types to include in the dashboard</li>
                                        <li className={"mb-2"}>Use the search bar in the filter modal to quickly find specific area types</li>
                                        <li className={"mb-2"}>Click <strong>"Clear All"</strong> to reset all filters</li>
                                        <li className={"mb-2"}>Click <strong>"Submit"</strong> to apply your filter selections</li>
                                        <li className={"mb-2"}>A small indicator appears on the filter button when filters are active</li>
                                    </ul>
                                </div>

                                <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-map-pin me-2 text-primary"}></i>Campus Selection
                                    </h5>
                                    <p className={"mb-2"}>Use the campus dropdown in the header to switch between different campuses:</p>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>Select a different campus to view its dashboard data</li>
                                        <li className={"mb-2"}>All charts and tables will update to show data for the selected campus</li>
                                    </ul>
                                </div>

                                <div className={"mb-3"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-settings me-2 text-primary"}></i>Additional Tips
                                    </h5>
                                    <ul className={"list-unstyled ms-4"}>
                                        <li className={"mb-2"}>The dashboard automatically refreshes every 10 minutes with the latest data</li>
                                        <li className={"mb-2"}>Click on resident names, caregiver names, or area names to navigate to their respective profile pages</li>
                                        <li className={"mb-2"}>Export buttons are available on the Resident and Caregiver review tables to download data as CSV files</li>
                                        <li className={"mb-2"}>Review Index colors help quickly identify residents with high alarm volumes that may need attention</li>
                                        <li className={"mb-2"}>Use the hourly chart to identify peak alarm times and adjust staffing accordingly</li>
                                        <li className={"mb-2"}>Compare shift performance using the Areas by Shift table to identify areas for improvement</li>
                                        <li className={"mb-2"}>The dashboard focuses on pendant and push/pull cord alerts under 60 minutes, excluding common areas</li>
                                    </ul>
                                </div>
                            </div>
                            <div className={"modal-footer"}>
                                <button type={"button"} className={"btn btn-secondary"} data-bs-dismiss={"modal"}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer
                    left={<h5 className={"mt-2 me-auto"}>* Stats based on 28 days, including only pendant & push/pull
                        cord alerts {"<"} 60 minutes, and excluding common areas</h5>}/>
            </div>
        </>
    );
};

export default MainDash;